import {
  DataType,
  Form,
  FormDialogMode,
  FormOptions,
  Section,
  useNotify,
  UserPermission
} from '@globaleris/ui.library'
import { Box, Button, Grid, makeStyles, useTheme } from '@material-ui/core'
import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useHistory } from 'react-router-dom'
import {
  EnumEstadoCiclo,
  EnumEstadoFactura,
  EnumTipoAgrupacion,
  EnumTipoCicloFacturacion,
  EnumTipoFacturacion,
  EnumTipoRefacturacion,
  EnumTipoSujeto
} from '../../../api'
import {
  InvoiceAnnexedParamModel,
  InvoiceDetailResponseModel
} from '../../../api/Argentia/endpoints/invoices/model/invoices'
import { SettlementBatchRequestModel } from '../../../api/Argentia/endpoints/settlementBatchs/model/settlementBatchs'
import {
  AdjustmentConceptTableModel,
  SettlementsInvoicesKPIsResponseModel,
  SettlementTableModel
} from '../../../api/Argentia/endpoints/settlements/model/settlements'
import { AnnexedParameters } from '../../../api/Argentia/enums'
import { EnumDominioTarea } from '../../../api/oData/enums/enumDominioTarea'
import { EnumEstadoLiquidacion } from '../../../api/oData/enums/enumEstadoLiquidacion'
import { EnumTipoLoteLiquidacion } from '../../../api/oData/enums/enumTipoLoteLiquidacion'
import Page, { PageLayout } from '../../../components/page/page'
import PageCenterColumn from '../../../components/page/pageCenterColumn'
import PageHeader from '../../../components/page/pageHeader'
import { AnnexedParamCustomFormDialog } from '../../../components/shared/annexedParamCustomFormDialog/annexedParamCustomFormDialog'
import { CardActionProps } from '../../../components/shared/cardActions/cardActions'
import { DropzoneDialog } from '../../../components/shared/dropzoneDialog'
import {
  FormStateConfig,
  InputFieldConfig
} from '../../../components/shared/genericCustomFormDialog/genericCustomFormDialog.model'
import { LandingKpiCard } from '../../../components/shared/landingKpiCard/landingKpiCard'
import {
  ObjectionsDialog,
  ObjectionsDialogTab
} from '../../../components/shared/objectionsDialog/objectionsDialog'
import { ButtonOption } from '../../../components/shared/splitButton/splitButton'
import { envConfig } from '../../../config/envConfig'
import { enumToSelectOptionArray, enumValueToKey } from '../../../helpers'
import { getUserPermissions, hasPermission } from '../../../helpers/permissionsHelper'
import { FormDialogDefinitionProps, useConfirmationDialog, useFormDialog } from '../../../hooks'
import { NonAuthRoutes } from '../../../model'
import { ConfirmationDialogs } from '../../../model/enums/confirmationDialogs'
import { InformationDialogs } from '../../../model/enums/informationDialogs'
import { Pages, Resources } from '../../../model/RBAC/constants'
import { getCustomerDomain } from '../../../redux'
import { terminateObjection } from '../../../services/objections.services'
import {
  fetchGlobalInvoiceCycleObjections,
  fetchInvoiceCycleExecution,
  getReportId
} from '../../invoiceCycles/invoiceCycles.service'
import { ObjectionTableModel } from '../../invoicesPage/invoicesPage.Model'
import {
  addInvoiceAnnexedParam,
  fetchInvoiceAnnexedParam,
  fetchObjectionsByInvoiceID,
  revertInvoicesToDraft,
  saveObjection,
  updateInvoiceAnnexedParam
} from '../../invoicesPage/invoicesPage.service'
import { createSettlementBatch } from '../../settlementBatchs/settlementBatchs.services'
import {
  fetchObjectionsBySettlementID,
  getInvoiceCycleByID,
  getSettlementsInvoicesLandingKpis,
  getSettlementsTableData,
  searchAdjustmentConcepts,
  uploadInvoiceCodesFile
} from '.././settlements.service'
import { AdjustmentConceptsTableCard } from '../components/adjustmentConceptsTableCard/adjustmentConceptsTableCard'
import { CloseExecutionCycleDialog } from '../components/closeExecutionCycleDialog'
import { GlobalObjectionsCard } from '../components/globalObjectionsCard/globalObjectionsCard'
import { RegenerateExecutionCycleDialog } from '../components/regenerateExecutionCycleDialog/regenerateExecutionCycleDialog'
import { SearchFilterCard } from '../components/searchFilterCard/searchFilterCard'
import { SettlementsTableCard } from '../components/settlementsTableCard/settlementsTableCard'
import { TerminateObjectionsDialog } from '../components/terminateObjectionsDialog/terminateObjectionsDialog'
import { useCloseExecutionCycleDialog, useExecutionCycleActions } from '../hooks'
import {
  completeDataTableActions,
  mapFiltersToSearchRequest,
  mapFormDataToInvoiceAnnexedParam,
  mapGlobalObjectionsResponseToTableModel,
  mapInvoiceCodesFileUploadRequest,
  mapObjectionsResponseToTableModel,
  mapSelectedRowsToSettlementBatchRequestModel,
  mapSettlementTableServiceToModel
} from '../settlements.mapper'
import {
  AdjustmentConceptSearchRequestModel,
  DropzoneDialogInvoiceForm,
  EnumSettlementBatchMode,
  EnumSettlementSearchType,
  EnumSettlementTableCustomActionConfig,
  EnumTipoOperacionMasivaCiclos,
  GlobalObjectionTableModel,
  SettlementFormSearchFilter,
  SettlementSearchRequestModel
} from '../settlements.models'
import { styles } from '../settlements.styles'
import { SettlementsInvoicesKpiLeftContent } from './components/settlementInvoicesKpiLeftContent/settlementInvoicesKpiLeftContent'
import {
  SettlementInvoiceKpiType,
  SettlementsInvoicesKpiRightContent
} from './components/settlementInvoicesKpiRightContent/settlementInvoicesKpiRightContent'

interface SettlementsPageProps {
  location?: {
    state: {
      executionCycleId?: number
      invoiceCycleId?: number
      invoiceCycleType?: EnumTipoCicloFacturacion
      subjectType?: EnumTipoSujeto
      billingType?: EnumTipoFacturacion
      reInvoiceType?: EnumTipoRefacturacion
      groupingType?: EnumTipoAgrupacion
      finishedExecutionCycle?: boolean
    }
  }
}

const initialFormStateConfig: FormStateConfig = {
  formMode: FormDialogMode.ADD,
  open: false,
  inputFields: [] as InputFieldConfig[],
  title: ''
}

export const SettlementsInvoicesPage: React.FC<SettlementsPageProps> = props => {
  const [t] = useTranslation([
    'globalerisLibrary',
    'enums',
    'settlements',
    'settlementsInvoices',
    'objectionsDataTable',
    'invoicesPage'
  ])
  const { getInformation, getConfirmation, closeConfirmation } = useConfirmationDialog()
  const { openForm, closeForm, updateFormDefinition, CustomFormDialog, formProps } = useFormDialog()
  const { info, error } = useNotify()
  const history = useHistory()

  const currentTheme = useTheme()
  const useStyles = makeStyles(styles(currentTheme))
  const classes = useStyles()

  // kpis
  const [isFetchingKpis, setIsFetchingKpis] = useState<boolean>(false)
  const [landingKpis, setLandingKpis] = useState<SettlementsInvoicesKPIsResponseModel | undefined>(
    undefined
  )

  // execution cycle detail (provides reInvoiceType, groupingType, dates for KPI card)
  const [executionData, setExecutionData] = useState<any>(null)
  const [isFetchingExecution, setIsFetchingExecution] = useState<boolean>(false)

  const cycleStatus: EnumEstadoCiclo | null = executionData?.status ?? null

  const [openCard, setOpenCard] = useState<
    'objections' | 'settlements' | 'issued' | 'search' | 'error' | null
  >(null)

  // objections
  const [objections, setObjections] = useState<GlobalObjectionTableModel[] | undefined>(undefined)
  const [isFetchingObjections, setIsFetchingObjections] = useState<boolean>(false)
  const [isLoadingObjections, setIsLoadingObjections] = useState<boolean>(false)
  const [isFetchingInvoiceCycle, setIsFetchingInvoiceCycle] = useState<boolean>(true)

  // Adjustment concepts (Refacturación por Ajustes / LG_GENERAR_FACTURA = 0)
  const [adjustmentConceptsData, setAdjustmentConceptsData] = useState<
    AdjustmentConceptTableModel[] | undefined
  >(undefined)
  const [isFetchingAdjustmentConcepts, setIsFetchingAdjustmentConcepts] = useState<boolean>(false)

  // settlements invoices
  const [isFetchingSettlementsData, setIsFetchingSettlementsData] = useState<boolean>(false)
  const [settlementsTableData, setSettlementsTableData] = useState<SettlementTableModel[]>([])
  const [settlementsIsBelowMaxQueryLimit, setSettlementsIsBelowMaxQueryLimit] =
    useState<boolean>(false)
  const [settlementsIsEmpty, setSettlementsIsEmpty] = useState<boolean>(true)
  const [customSelectedRowsMode, setCustomSelectedRowsMode] = useState<boolean>(false)

  // External row selection control
  const [selectedSettlementsData, setSelectedSettlementsData] = useState<SettlementTableModel[]>([])

  // settlements invoices Objections Dialog
  const [invoiceRow, setInvoiceRow] = useState<SettlementTableModel | null>(null)
  const [settlementInvoicesObjections, setSettlementInvoicesObjections] = useState<
    ObjectionTableModel[] | undefined
  >(undefined)
  const [showObjectionsDialog, setShowObjectionsDialog] = useState<boolean>(false)
  // error settlements with invoice: settlement objections tab, loaded when the tab is selected
  const [settlementTabObjections, setSettlementTabObjections] = useState<
    ObjectionTableModel[] | undefined
  >(undefined)
  const [isSettlementTabLoaded, setIsSettlementTabLoaded] = useState<boolean>(false)

  // terminate objections dialog
  const [showTerminateObjectionsDialog, setShowTerminateObjectionsDialog] = useState<boolean>(false)
  const [terminateObjectionsRows, setTerminateObjectionsRows] = useState<SettlementTableModel[]>([])

  // comments dialog
  const [commentDialogConfig, setCommentDialogConfig] =
    useState<FormStateConfig>(initialFormStateConfig)

  // bulk load dialog
  const [showBulkLoadDialog, setShowBulkLoadDialog] = useState<boolean>(false)
  const [dropzoneFormData, setDropzoneFormData] = useState<DropzoneDialogInvoiceForm>({
    operationType: EnumTipoLoteLiquidacion.Repintado
  })
  const [invoiceCycleType, setInvoiceCycleType] = useState<EnumTipoCicloFacturacion | undefined>(
    undefined
  )

  const openSettlements = useMemo(() => {
    return settlementsTableData.filter(
      settlement =>
        settlement.state === EnumEstadoLiquidacion.Abierta ||
        settlement.state === EnumEstadoLiquidacion.Pendiente
    )
  }, [settlementsTableData])

  // navigation params
  const executionCycleId = props.location?.state?.executionCycleId
  const [invoiceCycleId, setInvoiceCycleId] = useState<number | undefined>(
    props.location?.state?.invoiceCycleId
  )
  const groupingType = props.location?.state?.groupingType
  const reInvoiceType = props.location?.state?.reInvoiceType
  const [subjectType, setSubjectType] = useState<EnumTipoSujeto | undefined>(undefined)
  const [billingType, setBillingType] = useState<EnumTipoFacturacion | undefined>(undefined)
  const [reInvoiceTypeState, setReInvoiceTypeState] = useState<EnumTipoRefacturacion | undefined>(
    reInvoiceType
  )
  const [generateInvoice, setGenerateInvoice] = useState<boolean>(false)
  const [invoiceCycleDescription, setInvoiceCycleDescription] = useState<string | undefined>(
    undefined
  )

  const [showSearchFilter, setShowSearchFilter] = useState<boolean>(false)
  const [searchFilters, setSearchFilters] = useState<SettlementFormSearchFilter>({
    searchType: EnumSettlementSearchType.Contract,
    searchValue: ''
  })

  // KPI card selection state
  const [activeKpi, setActiveKpi] = useState<SettlementInvoiceKpiType | null>(null)

  if (!executionCycleId) {
    history.goBack()
    return null
  }

  const isFinishedExecutionCyclePage = props.location?.state?.finishedExecutionCycle ?? false

  // Base search configuration
  // debe validarse que invoiceCycleType, subjectType y billingType no sean undefined antes de usar esta constante para evitar errores en tiempo de ejecución
  const baseSearchConfig = useMemo(
    () => ({
      executionCycleId: executionCycleId,
      invoiceCycleType: invoiceCycleType!,
      subjectType: subjectType!,
      billingType: billingType!,
      cycleState: EnumEstadoCiclo.PdteValidarFacturas
    }),
    [executionCycleId, invoiceCycleType, subjectType, billingType]
  )

  // #region Handlers Starts

  // #region ------------------- Header Action Buttons Handlers -----------------------------//

  const {
    handleRegenerateExecutionCycle,
    closeExecutionCycle,
    regenerateDialogOpen,
    handleRegenerateDialogClose,
    regenerateInitialData
  } = useExecutionCycleActions({
    executionCycleId,
    invoiceCycleId: invoiceCycleId!,
    invoiceCycleDescription,
    invoiceCycleType,
    subjectType,
    billingType,
    currentCycleState: EnumEstadoCiclo.PdteValidarFacturas,
    executionInitialData: {
      startDate: executionData?.startDate,
      endDate: executionData?.endDate,
      invoiceCycleSubtype: executionData?.subtype
    }
  })

  const { isOpen, openDialog, closeDialog, onCloseWithInvoices, onCloseWithoutInvoices } =
    useCloseExecutionCycleDialog({ closeExecutionCycle })
  // #endregion ------------------- Header Action Buttons Handlers -----------------------------//

  // #region ------------------- Footer Action Buttons Handlers -----------------------------//

  const handleViewClickReport = async () => {
    // Validate required parameters
    if (!invoiceCycleType || !subjectType || !billingType) {
      error(t('settlementsInvoices:notifications.missingReportParameters'))
      return
    }

    try {
      const response = await getReportId(subjectType, billingType, invoiceCycleType)

      if (response.success && response.data?.reportId) {
        history.push(NonAuthRoutes.invoiceCycleReport, {
          executionCycleId,
          reportId: response.data.reportId
        })
      } else {
        error(t('settlementsInvoices:fetchResponse.getReportId.error'))
      }
    } catch (err) {
      error(t('settlementsInvoices:fetchResponse.getReportId.error'))
    }
  }

  const handleViewSearchCard = () => {
    setShowSearchFilter(!showSearchFilter)
    if (!showSearchFilter) {
      setCustomSelectedRowsMode(true)
      if (openCard === 'objections') {
        setOpenCard(null)
        setSettlementsTableData([])
      }
    }
  }

  const handleSearchFilterSubmit = (formState: SettlementFormSearchFilter) => {
    setSearchFilters(formState)

    const isAnnulationOrRefacturation =
      billingType === EnumTipoFacturacion.Anuladora ||
      billingType === EnumTipoFacturacion.Refacturacion

    if (!subjectType || (!isAnnulationOrRefacturation && (!invoiceCycleType || !billingType))) {
      return
    }

    const searchRequest = mapFiltersToSearchRequest(formState, baseSearchConfig)

    setIsFetchingSettlementsData(true)
    setOpenCard('search')

    getSettlementsTableData(searchRequest)
      .then(res => {
        if (res.success && res.data) {
          res.data.settlements.length === 0 && setSettlementsTableData([])
          if (res.data.exceedsLimit) {
            error(
              t('settlementsInvoices:notifications.fetchFilteredSettlements.exceedsLimitRefine', {
                total: res.data.totalCount
              })
            )
          } else if (res.data.totalCount > 0) {
            setSettlementsTableData(
              completeDataTableActions(
                mapSettlementTableServiceToModel(
                  res.data.settlements,
                  reInvoiceTypeState === EnumTipoRefacturacion.AnuladoraNueva
                )
              )
            )

            if (res.data.exceedsLimit) {
              info(
                t('settlementsInvoices:notifications.fetchFilteredSettlements.exceedsLimit', {
                  shown: res.data.maxResults,
                  total: res.data.totalCount
                })
              )
            }
          } else {
            setSettlementsTableData([])
            error(t('settlementsInvoices:notifications.fetchFilteredSettlements.noResults'))
          }
        } else if (!res.success) {
          error(
            res.error?.message ??
              t('settlementsInvoices:notifications.fetchFilteredSettlements.noResults')
          )
        }
      })
      .finally(() => setIsFetchingSettlementsData(false))
  }

  const handleCancelSearch = () => {
    setShowSearchFilter(false)
  }

  const handleSettlementsSelectionChange = useCallback(
    (selectedRows: SettlementTableModel[], selectedIndices: number[]) => {
      setSelectedSettlementsData(selectedRows)
    },
    []
  )

  const handleInvoicesSplitButtonClick = async (option: ButtonOption) => {
    let okToProceed = false
    let settlementBatchType: EnumTipoLoteLiquidacion = EnumTipoLoteLiquidacion.EmisionAutomatica

    switch (option.id) {
      case EnumSettlementBatchMode.ISSUE_CUSTOM_SELECTION:
        if (selectedSettlementsData.length > 0) {
          await handleIssueSelected(selectedSettlementsData)
        }
        return
      case EnumSettlementBatchMode.ISSUE_ALL:
        await getConfirmation(ConfirmationDialogs.INVOICE_CYCLE_ISSUE_ALL).then(res => {
          okToProceed = true
          closeConfirmation()
        })
        break
      case EnumSettlementBatchMode.ISSUE_WITHOUT_OBJECTIONS:
        await getConfirmation(ConfirmationDialogs.INVOICE_CYCLE_ISSUE_WITHOUT_OBJECTIONS).then(
          res => {
            okToProceed = true
            settlementBatchType = EnumTipoLoteLiquidacion.EmisionSinObjeciones
            closeConfirmation()
          }
        )
        break
      case EnumSettlementBatchMode.BULK_LOAD:
        setShowBulkLoadDialog(true)
        return
      case EnumSettlementBatchMode.ISSUE_WITH_DATE_RANGE:
        // Handle invoice with date range - with or without selection
        let invoiceIdsForDateRange: number[] = []
        let lotTypeForDateRange: EnumTipoLoteLiquidacion = EnumTipoLoteLiquidacion.EmisionAutomatica

        // Si hay selección, validar facturas emitidas primero
        if (selectedSettlementsData.length > 0) {
          const validation = await validateIssuedInvoices(selectedSettlementsData)
          if (!validation.canProceed) return

          invoiceIdsForDateRange = validation.filteredRows.map(row => row.invoiceId!)
          lotTypeForDateRange = EnumTipoLoteLiquidacion.Emision
        }

        await getConfirmation(ConfirmationDialogs.INVOICE_CYCLE_ISSUE_WITH_DATE_RANGE).then(res => {
          openForm({
            title: t('settlementsInvoices:forms.issueWithDateRangeForm.title'),
            definition: issueWithDateRangeFormDef,
            onClose: formData =>
              handleIssueWithDateRangeDialogClose(
                option.id as EnumSettlementBatchMode,
                invoiceIdsForDateRange,
                lotTypeForDateRange,
                formData
              ),
            mode: FormDialogMode.ADD
          })
          closeConfirmation()
        })
        return
      default:
        break
    }

    if (okToProceed) {
      const request: SettlementBatchRequestModel = mapSelectedRowsToSettlementBatchRequestModel(
        [],
        [],
        settlementBatchType,
        executionCycleId,
        option.id as EnumSettlementBatchMode
      )
      createSettlementBatchAsync(request)
      history.push(NonAuthRoutes.invoiceCycleProcessing, {
        executionCycleId,
        invoiceCycleId,
        cycleName: invoiceCycleDescription,
        originState: EnumEstadoCiclo.PdteValidarFacturas
      })
    }
  }

  const handleIssueWithDateRangeDialogClose = useCallback(
    (
      optionId: EnumSettlementBatchMode,
      invoiceIds: number[],
      type: EnumTipoLoteLiquidacion,
      formData?: any
    ) => {
      if (formData) {
        const request: SettlementBatchRequestModel = mapSelectedRowsToSettlementBatchRequestModel(
          [],
          invoiceIds,
          type,
          executionCycleId,
          optionId,
          formData
        )
        createSettlementBatchAsync(request)
        history.push(NonAuthRoutes.invoiceCycleProcessing, {
          executionCycleId,
          invoiceCycleId,
          cycleName: invoiceCycleDescription,
          originState: EnumEstadoCiclo.PdteValidarFacturas
        })
      }
      closeForm()
    },
    []
  )

  const handleBulkLoadSave = useCallback(
    async (files: File[]) => {
      const request = await mapInvoiceCodesFileUploadRequest(
        files[0],
        executionCycleId,
        dropzoneFormData.operationType
      )
      const response = await uploadInvoiceCodesFile(request)

      if (response.success && response.data?.fileProcessedOK) {
        info(t('settlementsInvoices:fetchResponse.uploadInvoiceCodesFile.success'))
        setDropzoneFormData({} as DropzoneDialogInvoiceForm)
        setShowBulkLoadDialog(false)
        history.push(NonAuthRoutes.invoiceCycleProcessing, {
          executionCycleId,
          invoiceCycleId,
          cycleName: invoiceCycleDescription,
          originState: EnumEstadoCiclo.PdteValidarFacturas,
          invoiceCycleType,
          subjectType,
          billingType
        })
      } else if (response.success && !response.data?.fileProcessedOK) {
        error(t('settlementsInvoices:fetchResponse.uploadInvoiceCodesFile.error'))
      } else {
        error(
          response.error?.message ??
            t('settlementsInvoices:fetchResponse.uploadInvoiceCodesFile.error')
        )
      }
    },
    [
      executionCycleId,
      dropzoneFormData.operationType,
      invoiceCycleId,
      invoiceCycleDescription,
      invoiceCycleType,
      subjectType,
      billingType
    ]
  )

  // #endregion ------------------- Footer Action Buttons Handlers -----------------------------//

  // #region --------------------------- Objections Handlers -----------------------------------//

  const handleTerminateObjection = async (
    row: ObjectionTableModel,
    closeObjectionsDialog: boolean = false
  ) => {
    // terminateObjections is shared between ObjectionsCard and ObjectionsDialog, in the latter we need to close the dialog after terminating the objection
    getConfirmation(ConfirmationDialogs.TERMINATE_OBJECTION).then(async () => {
      await patchTerminateObjection(row).then(() => {
        if (closeObjectionsDialog) {
          let refreshData: boolean = true
          handleObjectionsDialogClose(refreshData)
        } else {
          fetchSettlementBatchsObjections()
        }
      })
      closeConfirmation()
    })
  }

  // #endregion -------------------------- Objections Handlers ---------------------------------//

  // #region --------------------------- SettlementsTable Handlers -----------------------------------//

  const handleViewInvoiceObjections = useCallback(async (settlementRow: SettlementTableModel) => {
    setShowObjectionsDialog(true)
    setInvoiceRow(settlementRow)
    const response = await fetchObjectionsByInvoiceID(settlementRow.invoiceId!).then(res => {
      if (res.success && res.data) {
        return res.data
      } else {
        error(t('settlementsInvoices:fetchResponse.fetchInvoiceObjections.error'))
        return []
      }
    })
    setSettlementInvoicesObjections(mapObjectionsResponseToTableModel(response))
  }, [])

  const handleViewErrorSettlementObjections = useCallback(
    async (settlementRow: SettlementTableModel) => {
      setShowObjectionsDialog(true)
      setInvoiceRow(settlementRow)
      setIsLoadingObjections(true)
      // En error: si hay factura se muestran sus objeciones, si no las de la liquidación
      const response = await (
        settlementRow.invoiceId
          ? fetchObjectionsByInvoiceID(settlementRow.invoiceId)
          : fetchObjectionsBySettlementID(settlementRow.id)
      ).then(res => {
        if (res.success && res.data) {
          return res.data
        } else {
          error(t('settlementsInvoices:fetchResponse.fetchInvoiceObjections.error'))
          return []
        }
      })
      setSettlementInvoicesObjections(mapObjectionsResponseToTableModel(response))
      setIsLoadingObjections(false)
    },
    [error, t]
  )

  const handleViewInvoiceComments = useCallback(async (settlementRow: SettlementTableModel) => {
    setInvoiceRow(settlementRow)

    await fetchInvoiceAnnexedParam(settlementRow.invoiceId!).then(res => {
      if (res.success && res.data) {
        const invoiceAnnexes = res.data
        const commentAnnexedParam = invoiceAnnexes.find(
          annex => annex.annexedParam === AnnexedParameters.COMENTARIOS
        )
        const isReadOnly = settlementRow?.invoiceState !== EnumEstadoFactura.Borrador

        setCommentDialogConfig(createCommentDialogConfig(commentAnnexedParam, isReadOnly))
      } else {
        error(t('settlementsInvoices:fetchResponse.fetchInvoiceAnnexedParam.error'))
      }
    })
  }, [])

  const handleManualInvoiceEdit = useCallback(
    (settlementRow: SettlementTableModel) => {
      history.push(NonAuthRoutes.InvoicesEdit, {
        invoiceData: {
          id: settlementRow.invoiceId,
          code: settlementRow.invoiceCode ?? ''
        } as InvoiceDetailResponseModel
      })
    },
    [history]
  )

  const onSaveObjection = async (
    row: ObjectionTableModel,
    invoiceID?: number,
    settlementID?: number
  ) => {
    let refreshData: boolean = false
    if (!invoiceID && !settlementID) {
      error(t('settlementsInvoices:fetchResponse.saveObjection.error'))
      return
    }
    await saveObjection(row, invoiceID, settlementID)
      .then(response => {
        if (response.success) {
          refreshData = true
          info(t('settlementsInvoices:fetchResponse.saveObjection.success'))
        } else {
          error(t('settlementsInvoices:fetchResponse.saveObjection.error'))
        }
      })
      .finally(() => {
        handleObjectionsDialogClose(refreshData)
      })
  }

  /**
   * Valida si el valor del KPI excede el límite de consulta
   * @param kpiValue - Valor del KPI a validar
   * @returns true si puede proceder, false si excede el límite
   */
  const validateKpiQueryLimit = useCallback(
    async (kpiValue: number): Promise<boolean> => {
      if (kpiValue > envConfig.invoiceCycleConfig.maxQueryItems) {
        await getInformation(InformationDialogs.LIMIT_QUERY_RESULTS_SEARCHFILTER).then(() => {
          closeConfirmation()
        })
        return false
      }
      return true
    },
    [getInformation, closeConfirmation]
  )

  const handleRestartModesButtonClick = useCallback(() => {
    setActiveKpi(null)
    setCustomSelectedRowsMode(false)
    setOpenCard(null)
    setSettlementsTableData([])
  }, [])

  const fetchSettlementBatchsObjections = useCallback(() => {
    setIsFetchingObjections(true)
    return fetchGlobalInvoiceCycleObjections(executionCycleId)
      .then(response => {
        if (response.success && response.data) {
          setObjections(mapGlobalObjectionsResponseToTableModel(response.data))
        }
      })
      .finally(() => {
        setIsFetchingObjections(false)
      })
  }, [executionCycleId])

  const handleViewObjectionsHistory = useCallback(() => {
    if (openCard === 'objections') {
      setOpenCard(null)
      setSettlementsTableData([])
    } else {
      setActiveKpi(null)
      setShowSearchFilter(false)
      setSettlementsTableData([])
      setOpenCard('objections')
      fetchSettlementBatchsObjections()
    }
  }, [openCard, fetchSettlementBatchsObjections])

  const fetchSettlementsTableData = useCallback(
    async (notIssued?: boolean) => {
      const isAnnulationOrRefacturation =
        billingType === EnumTipoFacturacion.Anuladora ||
        billingType === EnumTipoFacturacion.Refacturacion

      if (!subjectType || (!isAnnulationOrRefacturation && (!invoiceCycleType || !billingType))) {
        return
      }

      setIsFetchingSettlementsData(true)
      const request = mapFiltersToSearchRequest(
        {
          searchType: EnumSettlementSearchType.Contract,
          searchValue: ''
        },
        {
          ...baseSearchConfig,
          notIssued: notIssued
        }
      )
      await getSettlementsTableData(request)
        .then(res => {
          if (res?.success && res?.data) {
            const nonErrorSettlements = res.data.settlements.filter(
              s => s.state !== EnumEstadoLiquidacion.Error
            )
            setSettlementsTableData(
              completeDataTableActions(
                mapSettlementTableServiceToModel(
                  nonErrorSettlements,
                  reInvoiceTypeState === EnumTipoRefacturacion.AnuladoraNueva
                )
              )
            )
          }
        })
        .finally(() => setIsFetchingSettlementsData(false))
    },
    [invoiceCycleType, subjectType, billingType, baseSearchConfig, reInvoiceTypeState]
  )

  const fetchSettlementsWithObjections = useCallback(() => {
    const request = mapFiltersToSearchRequest(
      {
        searchType: EnumSettlementSearchType.Contract,
        searchValue: '',
        hasInvoiceObjections: true
      },
      baseSearchConfig
    )
    setIsFetchingSettlementsData(true)
    getSettlementsTableData(request)
      .then(res => {
        if (res?.success && res?.data) {
          setSettlementsTableData(
            completeDataTableActions(
              mapSettlementTableServiceToModel(
                res.data.settlements,
                reInvoiceTypeState === EnumTipoRefacturacion.AnuladoraNueva
              )
            )
          )
        }
      })
      .finally(() => setIsFetchingSettlementsData(false))
  }, [baseSearchConfig, reInvoiceTypeState])

  const fetchErrorSettlementsData = useCallback(async () => {
    if (!subjectType || !invoiceCycleType || !billingType) return
    setIsFetchingSettlementsData(true)
    const request: SettlementSearchRequestModel = {
      ...baseSearchConfig,
      maxResults: envConfig.invoiceCycleConfig.maxQueryItems,
      settlementState: EnumEstadoLiquidacion.Error
    }
    await getSettlementsTableData(request)
      .then(res => {
        if (res?.success && res?.data) {
          setSettlementsTableData(
            completeDataTableActions(mapSettlementTableServiceToModel(res.data.settlements))
          )
        }
      })
      .finally(() => setIsFetchingSettlementsData(false))
  }, [subjectType, invoiceCycleType, billingType, baseSearchConfig])

  const fetchAdjustmentConceptsData = useCallback(async () => {
    setAdjustmentConceptsData(undefined)
    setIsFetchingAdjustmentConcepts(true)
    const request: AdjustmentConceptSearchRequestModel = {
      executionCycleId: executionCycleId!,
      maxResults: envConfig.invoiceCycleConfig.maxQueryItems
    }
    await searchAdjustmentConcepts(request)
      .then(res => {
        if (res.success && res.data) {
          setAdjustmentConceptsData(res.data.concepts)
        }
      })
      .finally(() => setIsFetchingAdjustmentConcepts(false))
  }, [executionCycleId])

  // #region ------------------- KPI Click Handlers -----------------------------//
  /**
   * Refresca la tabla de settlements respetando el contexto actual (búsqueda, filtro, etc)
   */
  const refreshTableByContext = useCallback(() => {
    fetchSettlementInvoicesAnnouncements()

    const isAdjReInvoice =
      !isFetchingInvoiceCycle &&
      billingType === EnumTipoFacturacion.Refacturacion &&
      (reInvoiceTypeState === EnumTipoRefacturacion.AjusteProxima || !generateInvoice)

    if (isAdjReInvoice) {
      fetchAdjustmentConceptsData()
      return
    }

    if (openCard === 'search') {
      searchFilters && handleSearchFilterSubmit(searchFilters)
    } else if (openCard === 'settlements') {
      if (activeKpi === 'totalInvoicesWithObjections') {
        fetchSettlementsWithObjections()
      } else if (activeKpi === 'nonIssuedInvoices') {
        fetchSettlementsTableData(true)
      } else {
        fetchSettlementsTableData()
      }
    } else if (openCard === 'issued') {
      fetchSettlementsTableData(false)
    } else if (openCard === 'error') {
      fetchErrorSettlementsData()
    } else {
      fetchSettlementsTableData()
    }
  }, [
    openCard,
    activeKpi,
    searchFilters,
    isFetchingInvoiceCycle,
    billingType,
    generateInvoice,
    handleSearchFilterSubmit,
    fetchAdjustmentConceptsData,
    fetchSettlementsWithObjections,
    fetchSettlementsTableData,
    fetchErrorSettlementsData
  ])

  const handleKpiClick = useCallback(
    async (kpiType: SettlementInvoiceKpiType) => {
      // Si se hace click en el mismo KPI activo, lo deseleccionamos
      if (activeKpi === kpiType) {
        setActiveKpi(null)
        handleRestartModesButtonClick()
        return
      }
      const isAnnulationOrRefacturation =
        billingType === EnumTipoFacturacion.Anuladora ||
        billingType === EnumTipoFacturacion.Refacturacion

      if (
        !landingKpis ||
        !subjectType ||
        (!isAnnulationOrRefacturation && (!invoiceCycleType || !billingType))
      ) {
        return
      }

      setActiveKpi(kpiType)
      setSelectedSettlementsData([])

      // Ciclo de ajuste: refrescar conceptos en lugar de llamar a Search de liquidaciones
      if (
        billingType === EnumTipoFacturacion.Refacturacion &&
        (reInvoiceTypeState === EnumTipoRefacturacion.AjusteProxima || !generateInvoice)
      ) {
        setOpenCard('settlements')
        fetchAdjustmentConceptsData()
        return
      }

      switch (kpiType) {
        case 'totalInvoicesGenerated':
          // Ver todas las facturas generadas
          if (!(await validateKpiQueryLimit(landingKpis.totalInvoicesGenerated))) {
            setActiveKpi(null)
            return
          }
          setOpenCard('settlements')
          setCustomSelectedRowsMode(true)
          fetchSettlementsTableData()
          break
        case 'issuedInvoices':
          // Ver facturas emitidas
          if (!(await validateKpiQueryLimit(landingKpis.issuedInvoices))) {
            setActiveKpi(null)
            return
          }
          setOpenCard('issued')
          setCustomSelectedRowsMode(true)
          fetchSettlementsTableData(false) // notIssued = false
          break
        case 'nonIssuedInvoices':
          // Ver facturas pendientes (no emitidas)
          if (!(await validateKpiQueryLimit(landingKpis.nonIssuedInvoices))) {
            setActiveKpi(null)
            return
          }
          setOpenCard('settlements')
          setCustomSelectedRowsMode(true)
          fetchSettlementsTableData(true) // notIssued = true
          break
        case 'totalInvoicesWithObjections':
          // Ver facturas con objeciones
          if (!(await validateKpiQueryLimit(landingKpis.totalInvoicesWithObjections))) {
            setActiveKpi(null)
            return
          }
          setShowSearchFilter(false)
          setSettlementsTableData([])
          setOpenCard('settlements')
          setCustomSelectedRowsMode(true)
          fetchSettlementsWithObjections()
          break
        case 'errorSettlements':
          // Ver liquidaciones en estado Error
          if (!(await validateKpiQueryLimit(landingKpis.errorSettlements ?? 0))) {
            setActiveKpi(null)
            return
          }
          setShowSearchFilter(false)
          setSettlementsTableData([])
          setOpenCard('error')
          setCustomSelectedRowsMode(false)
          fetchErrorSettlementsData()
          break
      }
    },
    [
      activeKpi,
      landingKpis,
      invoiceCycleType,
      subjectType,
      billingType,
      generateInvoice,
      baseSearchConfig,
      validateKpiQueryLimit,
      fetchAdjustmentConceptsData,
      fetchSettlementsTableData,
      fetchSettlementsWithObjections,
      fetchErrorSettlementsData
    ]
  )
  // #endregion ------------------- KPI Click Handlers -----------------------------//

  const createSettlementBatchAsync = useCallback(async (request: SettlementBatchRequestModel) => {
    await createSettlementBatch(request).then(response => {
      if (response.success && response.data) {
        t('settlements:notifications.createSettlementBatch.success')
      } else {
        t('settlements:notifications.createSettlementBatch.error')
      }
    })
  }, [])

  const handleDeleteErrorSettlement = useCallback(
    async (selectedRows: SettlementTableModel[]) => {
      const request: SettlementBatchRequestModel = mapSelectedRowsToSettlementBatchRequestModel(
        selectedRows,
        [],
        EnumTipoLoteLiquidacion.Eliminacion,
        executionCycleId,
        EnumSettlementBatchMode.DELETE_ERROR_SETTLEMENTS
      )
      await createSettlementBatchAsync(request)
      history.push(NonAuthRoutes.invoiceCycleProcessing, {
        executionCycleId,
        invoiceCycleId,
        cycleName: invoiceCycleDescription,
        originState: EnumEstadoCiclo.PdteValidarFacturas
      })
    },
    [createSettlementBatchAsync, history, executionCycleId, invoiceCycleId, invoiceCycleDescription]
  )

  const handleIssueSelected = async (selectedRows: SettlementTableModel[]) => {
    setCustomSelectedRowsMode(true)

    const validation = await validateIssuedInvoices(selectedRows)
    if (!validation.canProceed) return

    let request: SettlementBatchRequestModel = mapSelectedRowsToSettlementBatchRequestModel(
      [],
      validation.filteredRows.map(row => row.invoiceId!),
      EnumTipoLoteLiquidacion.Emision,
      executionCycleId,
      EnumSettlementBatchMode.ISSUE_CUSTOM_SELECTION
    )
    createSettlementBatchAsync(request)
    history.push(NonAuthRoutes.invoiceCycleProcessing, {
      executionCycleId,
      invoiceCycleId,
      cycleName: invoiceCycleDescription,
      originState: EnumEstadoCiclo.PdteValidarFacturas
    })
  }

  const handleRegenerate = useCallback(
    async (selectedRows: SettlementTableModel[]) => {
      const validation = await validateIssuedInvoices(selectedRows)
      if (!validation.canProceed) return

      await getConfirmation(ConfirmationDialogs.INVOICE_CYCLE_REGENERATE_SETTLEMENTS_INVOICES).then(
        () => {
          const request: SettlementBatchRequestModel = mapSelectedRowsToSettlementBatchRequestModel(
            validation.filteredRows,
            [],
            EnumTipoLoteLiquidacion.Facturacion,
            executionCycleId,
            EnumSettlementBatchMode.REGENERATE_INVOICES
          )
          createSettlementBatchAsync(request)
          history.push(NonAuthRoutes.invoiceCycleProcessing, {
            executionCycleId,
            invoiceCycleId,
            cycleName: invoiceCycleDescription,
            originState: EnumEstadoCiclo.PdteValidarFacturas
          })
          closeConfirmation()
        }
      )
    },
    [createSettlementBatchAsync, history, executionCycleId, invoiceCycleId, invoiceCycleDescription]
  )

  const handleRepaint = useCallback(async (selectedRows: SettlementTableModel[]) => {
    const validation = await validateIssuedInvoices(selectedRows)
    if (!validation.canProceed) return

    await getConfirmation(ConfirmationDialogs.INVOICE_CYCLE_REPAINT_INVOICES).then(async () => {
      const request: SettlementBatchRequestModel = mapSelectedRowsToSettlementBatchRequestModel(
        validation.filteredRows,
        [],
        EnumTipoLoteLiquidacion.Repintado,
        executionCycleId,
        EnumSettlementBatchMode.REPAINT_INVOICES
      )
      createSettlementBatchAsync(request)
      history.push(NonAuthRoutes.invoiceCycleProcessing, {
        executionCycleId,
        invoiceCycleId,
        cycleName: invoiceCycleDescription,
        originState: EnumEstadoCiclo.PdteValidarFacturas
      })
      closeConfirmation()
    })
  }, [])

  const handleReject = useCallback(
    async (selectedRows: SettlementTableModel[]) => {
      const validation = await validateIssuedInvoices(selectedRows)
      if (!validation.canProceed) return

      await getConfirmation(ConfirmationDialogs.INVOICE_CYCLE_REJECT_INVOICES).then(() => {
        const request: SettlementBatchRequestModel = mapSelectedRowsToSettlementBatchRequestModel(
          validation.filteredRows,
          [],
          EnumTipoLoteLiquidacion.Eliminacion,
          executionCycleId,
          EnumSettlementBatchMode.REJECT_INVOICES
        )
        createSettlementBatchAsync(request)
        history.push(NonAuthRoutes.invoiceCycleProcessing, {
          executionCycleId,
          invoiceCycleId,
          cycleName: invoiceCycleDescription,
          originState: EnumEstadoCiclo.PdteValidarFacturas
        })
        closeConfirmation()
      })
    },
    [createSettlementBatchAsync, history, executionCycleId, invoiceCycleId, invoiceCycleDescription]
  )

  const handleRevertDiscarded = useCallback(
    async (selectedRows: SettlementTableModel[]) => {
      const discardedRows = selectedRows.filter(
        row => row.invoiceState === EnumEstadoFactura.Descartada && row.invoiceId
      )

      if (discardedRows.length === 0) return

      await getConfirmation(ConfirmationDialogs.INVOICE_CYCLE_REVERT_DISCARDED_INVOICES).then(
        async () => {
          const response = await revertInvoicesToDraft(discardedRows.map(row => row.invoiceId!))

          if (response.success) {
            info(t('settlementsInvoices:fetchResponse.revertInvoicesToDraft.success'))
            refreshTableByContext()
          } else {
            error(t('settlementsInvoices:fetchResponse.revertInvoicesToDraft.error'))
          }

          closeConfirmation()
        }
      )
    },
    [getConfirmation, info, error, t, refreshTableByContext, closeConfirmation]
  )

  // #region ------------------- Adjustment Concepts Group Handlers -----------------------------//

  // Wrappers de tipo para pasar AdjustmentConceptTableModel a los handlers de objeciones/comentarios
  // que esperan SettlementTableModel (sólo usan invoiceId e invoiceState)
  const handleViewConceptObjections = useCallback(
    async (row: AdjustmentConceptTableModel) => {
      setShowObjectionsDialog(true)
      setInvoiceRow({ invoiceId: row.invoiceId, id: row.settlementId } as SettlementTableModel)
      const response = await fetchObjectionsBySettlementID(row.settlementId!).then(res => {
        if (res.success && res.data) {
          return res.data
        } else {
          error(t('settlementsInvoices:fetchResponse.fetchInvoiceObjections.error'))
          return []
        }
      })
      setSettlementInvoicesObjections(mapObjectionsResponseToTableModel(response))
    },
    [error, t]
  )

  const handleViewConceptComments = useCallback(
    (row: AdjustmentConceptTableModel) =>
      handleViewInvoiceComments({
        invoiceId: row.invoiceId,
        invoiceState: row.conceptState ?? undefined
      } as SettlementTableModel),
    [handleViewInvoiceComments]
  )

  const handleRegenerateGroup = useCallback(
    async (_groupValue: any, leafRows: AdjustmentConceptTableModel[]) => {
      await getConfirmation(ConfirmationDialogs.INVOICE_CYCLE_REGENERATE_SETTLEMENTS_INVOICES).then(
        () => {
          const settlementRows = leafRows.map(r => ({ id: r.settlementId }) as SettlementTableModel)
          const request = mapSelectedRowsToSettlementBatchRequestModel(
            settlementRows,
            [],
            EnumTipoLoteLiquidacion.Facturacion,
            executionCycleId!,
            EnumSettlementBatchMode.REGENERATE_INVOICES
          )
          createSettlementBatchAsync(request)
          history.push(NonAuthRoutes.invoiceCycleProcessing, {
            executionCycleId,
            invoiceCycleId,
            cycleName: invoiceCycleDescription,
            originState: EnumEstadoCiclo.PdteValidarFacturas
          })
          closeConfirmation()
        }
      )
    },
    [createSettlementBatchAsync, history, executionCycleId, invoiceCycleId, invoiceCycleDescription]
  )

  const handleRepaintGroup = useCallback(
    async (_groupValue: any, leafRows: AdjustmentConceptTableModel[]) => {
      await getConfirmation(ConfirmationDialogs.INVOICE_CYCLE_REPAINT_INVOICES).then(async () => {
        const settlementRows = leafRows.map(r => ({ id: r.settlementId }) as SettlementTableModel)
        const request = mapSelectedRowsToSettlementBatchRequestModel(
          settlementRows,
          [],
          EnumTipoLoteLiquidacion.Repintado,
          executionCycleId!,
          EnumSettlementBatchMode.REPAINT_INVOICES
        )
        createSettlementBatchAsync(request)
        history.push(NonAuthRoutes.invoiceCycleProcessing, {
          executionCycleId,
          invoiceCycleId,
          cycleName: invoiceCycleDescription,
          originState: EnumEstadoCiclo.PdteValidarFacturas
        })
        closeConfirmation()
      })
    },
    [createSettlementBatchAsync, history, executionCycleId, invoiceCycleId, invoiceCycleDescription]
  )

  const handleRejectGroup = useCallback(
    async (_groupValue: any, leafRows: AdjustmentConceptTableModel[]) => {
      await getConfirmation(ConfirmationDialogs.INVOICE_CYCLE_REJECT_INVOICES).then(() => {
        const settlementRows = leafRows.map(r => ({ id: r.settlementId }) as SettlementTableModel)
        const request = mapSelectedRowsToSettlementBatchRequestModel(
          settlementRows,
          [],
          EnumTipoLoteLiquidacion.Eliminacion,
          executionCycleId!,
          EnumSettlementBatchMode.REJECT_INVOICES
        )
        createSettlementBatchAsync(request)
        history.push(NonAuthRoutes.invoiceCycleProcessing, {
          executionCycleId,
          invoiceCycleId,
          cycleName: invoiceCycleDescription,
          originState: EnumEstadoCiclo.PdteValidarFacturas
        })
        closeConfirmation()
      })
    },
    [createSettlementBatchAsync, history, executionCycleId, invoiceCycleId, invoiceCycleDescription]
  )

  // #endregion ------------------- Adjustment Concepts Group Handlers -----------------------------//

  const handleTerminateObjectionsClick = useCallback((selectedRows: SettlementTableModel[]) => {
    setTerminateObjectionsRows(selectedRows)
    setShowTerminateObjectionsDialog(true)
  }, [])

  const refreshCurrentInvoiceObjections = useCallback(async () => {
    if (showObjectionsDialog && (invoiceRow?.invoiceId || invoiceRow?.id)) {
      setIsLoadingObjections(true)
      const res =
        isSettlementObjections
          ? await fetchObjectionsBySettlementID(invoiceRow.id!)
          : await fetchObjectionsByInvoiceID(invoiceRow.invoiceId!)
      if (res.success && res.data) {
        setSettlementInvoicesObjections(mapObjectionsResponseToTableModel(res.data))
      }
      setIsLoadingObjections(false)
    }
  }, [showObjectionsDialog, invoiceRow?.invoiceId, invoiceRow?.id, openCard])

  const handleObjectionsDialogClose = useCallback(
    (refreshData: boolean = false) => {
      setShowObjectionsDialog(false)
      setSettlementInvoicesObjections(undefined)
      setSettlementTabObjections(undefined)
      setIsSettlementTabLoaded(false)
      if (refreshData) {
        refreshTableByContext()
      }
    },
    [refreshTableByContext]
  )

  const handleRefreshInvoicesAfterTerminate = useCallback(() => {
    setTerminateObjectionsRows([])
    setSelectedSettlementsData([])
    refreshTableByContext()
    refreshCurrentInvoiceObjections()
  }, [refreshTableByContext, refreshCurrentInvoiceObjections])

  const handleRefreshObjectionsDialogInvoices = useCallback(() => {
    if (openCard === 'settlements' || openCard === 'issued' || openCard === 'search') {
      refreshTableByContext()
    } else if (openCard === 'objections') {
      fetchSettlementInvoicesAnnouncements()
      fetchSettlementBatchsObjections()
    } else if (openCard === 'error') {
      fetchErrorSettlementsData()
    }
    refreshCurrentInvoiceObjections()
  }, [openCard, refreshTableByContext, refreshCurrentInvoiceObjections, fetchErrorSettlementsData])

  // #endregion -------------------------- SettlementsTable Handlers ---------------------------------//

  // #region ---------------------------  Comments Dialog Handlers -----------------------------------//

  const handleCommentDialogClose = (formData: any) => {
    setCommentDialogConfig(prevState => ({
      ...prevState,
      open: false
    }))

    if (!formData) return

    const annexedParamRequest = mapFormDataToInvoiceAnnexedParam(formData, invoiceRow?.invoiceId!)

    if (commentDialogConfig.formMode === FormDialogMode.ADD) {
      handleAddComment(annexedParamRequest)
    } else if (commentDialogConfig.formMode === FormDialogMode.EDIT) {
      handleUpdateComment(formData, annexedParamRequest)
    }
  }

  // #endregion ---------------------------  Comments Dialog Handlers -----------------------------------//

  // #endregion Handlers Ends

  // #region Functions Starts

  /**
   * Valida facturas emitidas y maneja confirmaciones
   * @returns Promise con { canProceed: boolean, filteredRows: SettlementTableModel[] }
   */
  const validateIssuedInvoices = async (
    selectedRows: SettlementTableModel[]
  ): Promise<{ canProceed: boolean; filteredRows: SettlementTableModel[] }> => {
    const issuedInvoices = selectedRows.filter(
      row => row.invoiceState === EnumEstadoFactura.Emitida
    )

    // Si todas están emitidas, mostrar mensaje informativo y cancelar
    if (issuedInvoices.length === selectedRows.length) {
      await getInformation(InformationDialogs.INVOICE_CYCLE_ISSUE_ALREADY_ISSUED_ALL).then(() => {
        closeConfirmation()
      })
      return { canProceed: false, filteredRows: [] }
    }

    // Si hay algunas emitidas, pedir confirmación
    if (issuedInvoices.length > 0) {
      let confirmed = false
      await getConfirmation(
        ConfirmationDialogs.INVOICE_CYCLE_ISSUE_ALREADY_ISSUED,
        t(
          `confirmDialogs:${enumValueToKey(ConfirmationDialogs, ConfirmationDialogs.INVOICE_CYCLE_ISSUE_ALREADY_ISSUED)}.message`,
          { issued: issuedInvoices.length, count: selectedRows.length - issuedInvoices.length }
        )
      ).then(() => {
        confirmed = true
        closeConfirmation()
      })

      if (confirmed) {
        // Filtrar solo las no emitidas
        const filteredRows = selectedRows.filter(
          row => row.invoiceState !== EnumEstadoFactura.Emitida
        )
        return { canProceed: true, filteredRows }
      }
      return { canProceed: false, filteredRows: [] }
    }

    // Ninguna emitida, continuar con todas
    return { canProceed: true, filteredRows: selectedRows }
  }

  // #endregion Functions Ends

  // #region Form Definition Starts
  const createCommentDialogConfig = useCallback(
    (
      commentAnnexedParam?: InvoiceAnnexedParamModel,
      isReadOnly: boolean = false
    ): FormStateConfig => ({
      inputFields: [
        {
          name: 'idInvoiceAnnexedParam',
          label: '',
          type: DataType.TEXT,
          display: false
        },
        {
          name: 'comments',
          label: t('settlementsInvoices:components.commentsDialog.inputs.comments'),
          type: DataType.LONGTEXT,
          readOnly: isReadOnly
        }
      ],
      formMode: isReadOnly
        ? FormDialogMode.VIEW
        : commentAnnexedParam
          ? FormDialogMode.EDIT
          : FormDialogMode.ADD,
      open: true,
      title: t('settlementsInvoices:components.commentsDialog.title'),
      okBtnLabel: t('settlementsInvoices:components.commentsDialog.buttons.addComment'),
      inputData: {
        idInvoiceAnnexedParam: commentAnnexedParam?.idInvoiceAnnexedParam,
        comments: commentAnnexedParam?.value || ''
      }
    }),
    [t]
  )

  const issueWithDateRangeFormDef: FormDialogDefinitionProps = useMemo(() => {
    return {
      id: 'issueWithDateRangeForm',
      sections: [
        {
          inputs: [
            {
              name: 'startDate',
              label: t('settlementsInvoices:forms.issueWithDateRangeForm.inputs.startDate'),
              type: DataType.DATE
            },
            {
              name: 'endDate',
              label: t('settlementsInvoices:forms.issueWithDateRangeForm.inputs.endDate'),
              type: DataType.DATE
            }
          ]
        }
      ]
    }
  }, [])

  useEffect(() => {
    updateFormDefinition(issueWithDateRangeFormDef)
  }, [issueWithDateRangeFormDef])

  const dropzoneFormOptions: FormOptions = {
    elevation: 0,
    columns: 1,
    analytics: {
      customer: getCustomerDomain(),
      view: t('settlementsInvoices:components.bulkLoadDialog.forms.analytics')
    }
  }

  const sections: Section[] = [
    {
      title: undefined,
      inputs: [
        {
          name: 'operationType',
          label: t('settlementsInvoices:components.bulkLoadDialog.forms.inputs.operationType'),
          type: DataType.SELECT,
          selectOptions: enumToSelectOptionArray(
            EnumTipoOperacionMasivaCiclos,
            'enums:EnumTipoOperacionMasivaCiclos'
          ),
          defaultValue: EnumTipoLoteLiquidacion.Repintado,
          onChange: (value: string | number) => {
            if (!value) return
            setDropzoneFormData(prev => ({
              ...prev,
              operationType: +value
            }))
          }
        }
      ]
    }
  ]

  // #endregion Form Definition Ends

  // #region DataTable Config Starts
  // #endregion DataTable Config Ends

  // #region FooterActions Config Starts
  // Indica si este ciclo debe mostrar conceptos de ajuste en lugar de facturas
  const isAdjustmentReInvoice =
    !isFetchingInvoiceCycle &&
    billingType === EnumTipoFacturacion.Refacturacion &&
    (reInvoiceTypeState === EnumTipoRefacturacion.AjusteProxima || !generateInvoice)

  // Objeciones de liquidación: en ajustes siempre, y en error solo si la fila no tiene factura
  const isSettlementObjections =
    isAdjustmentReInvoice || (openCard === 'error' && !invoiceRow?.invoiceId)

  // En error con factura y liquidación: pestaña Factura (carga inicial) y pestaña Liquidación
  // (se carga al seleccionarla para no hacer siempre 2 llamadas)
  const hasErrorObjectionsTabs =
    openCard === 'error' && !isAdjustmentReInvoice && !!invoiceRow?.invoiceId && !!invoiceRow?.id

  const handleActivateSettlementObjectionsTab = async () => {
    if (isSettlementTabLoaded || !invoiceRow?.id) return
    setIsSettlementTabLoaded(true)
    const response = await fetchObjectionsBySettlementID(invoiceRow.id).then(res => {
      if (res.success && res.data) {
        return res.data
      } else {
        error(t('settlementsInvoices:fetchResponse.fetchInvoiceObjections.error'))
        return []
      }
    })
    setSettlementTabObjections(mapObjectionsResponseToTableModel(response))
  }

  const objectionsDialogTabs: ObjectionsDialogTab[] | undefined = hasErrorObjectionsTabs
    ? [
        {
          id: 'invoice',
          label: t('settlementsInvoices:dialogs.invoicesObjections.tabs.invoice'),
          objectionsData: isLoadingObjections ? undefined : settlementInvoicesObjections,
          taskDomain: EnumDominioTarea.Factura,
          onRowAdd: row => onSaveObjection(row, invoiceRow?.invoiceId!)
        },
        {
          id: 'settlement',
          label: t('settlementsInvoices:dialogs.invoicesObjections.tabs.settlement'),
          objectionsData: settlementTabObjections,
          taskDomain: EnumDominioTarea.Liquidacion,
          onRowAdd: row => onSaveObjection(row, undefined, invoiceRow?.id),
          onActivate: handleActivateSettlementObjectionsTab
        }
      ]
    : undefined

  const footerButtons: CardActionProps[] = useMemo(() => {
    let buttons: CardActionProps[] = [
      {
        label: t('settlementsInvoices:components.kpiCard.button.viewClickReport'),
        handleActionClick: handleViewClickReport,
        color: 'primary'
      },
      {
        label: t('settlementsInvoices:components.kpiCard.button.objectionsHistory'),
        handleActionClick: handleViewObjectionsHistory,
        color: 'primary'
      }
    ]

    if (!isAdjustmentReInvoice) {
      buttons = [
        ...buttons,
        {
          label: t('settlementsInvoices:components.kpiCard.button.viewSearchCard'),
          handleActionClick: handleViewSearchCard,
          color: 'primary'
        }
      ]
    }

    const splitButtonOptions: any[] = []

    // Si hay selección, agregar opción personalizada PRIMERO
    if (selectedSettlementsData.length > 0) {
      splitButtonOptions.push({
        label: t('settlementsInvoices:components.kpiCard.button.issueCustom', {
          count: selectedSettlementsData.length
        }),
        name: 'issueCustomSelection',
        id: EnumSettlementBatchMode.ISSUE_CUSTOM_SELECTION
      })
    }

    // Opciones estándar
    splitButtonOptions.push(
      {
        label: t('settlementsInvoices:components.kpiCard.button.issueAll'),
        name: 'issueAll',
        id: EnumSettlementBatchMode.ISSUE_ALL
      },
      {
        label: t('settlementsInvoices:components.kpiCard.button.issueWithoutObjections'),
        name: 'issueWithObjections',
        id: EnumSettlementBatchMode.ISSUE_WITHOUT_OBJECTIONS
      },
      {
        label:
          selectedSettlementsData.length === 0
            ? t('settlementsInvoices:components.kpiCard.button.issueWithDateRange')
            : t('settlementsInvoices:components.kpiCard.button.issueWithDateRangeCustom', {
                count: selectedSettlementsData.length
              }),
        name: 'issueWithDateRange',
        id: EnumSettlementBatchMode.ISSUE_WITH_DATE_RANGE
      },
      {
        label: t('settlementsInvoices:components.kpiCard.button.bulkLoad'),
        name: 'bulkLoad',
        id: EnumSettlementBatchMode.BULK_LOAD
      }
    )

    buttons.push({
      splitButton: {
        options: splitButtonOptions,
        onClick: handleInvoicesSplitButtonClick,
        hasPermission: !isFinishedExecutionCyclePage
      }
    })

    return buttons
  }, [
    selectedSettlementsData,
    settlementsIsEmpty,
    settlementsIsBelowMaxQueryLimit,
    isFinishedExecutionCyclePage,
    isAdjustmentReInvoice,
    t,
    handleViewClickReport,
    handleViewObjectionsHistory,
    handleViewSearchCard,
    handleInvoicesSplitButtonClick
  ])
  // #endregion FooterActions Config Ends

  // #region Hooks Starts
  useEffect(() => {
    const invoiceCycleIdFromNav = props.location?.state?.invoiceCycleId

    // If already in nav state, fetch cycle info immediately (parallel with execution fetch)
    if (invoiceCycleIdFromNav) {
      fetchInvoiceCycleInfo(invoiceCycleIdFromNav)
    }

    fetchSettlementInvoicesAnnouncements()

    if (executionCycleId) {
      setIsFetchingExecution(true)
      fetchInvoiceCycleExecution(executionCycleId)
        .then(res => {
          if (res.success && res.data) {
            setExecutionData(res.data)
            if (res.data.reInvoiceType !== undefined) setReInvoiceTypeState(res.data.reInvoiceType)
            // invoiceCycleId comes embedded in the execution response
            if (!invoiceCycleIdFromNav && res.data.invoiceCycleId) {
              setInvoiceCycleId(res.data.invoiceCycleId)
              fetchInvoiceCycleInfo(res.data.invoiceCycleId)
            }
          }
        })
        .finally(() => setIsFetchingExecution(false))
    }
    // getObjectionsDescriptions()
  }, [])

  // Disparar fetch de conceptos de ajuste una vez que se conoce el tipo de ciclo
  useEffect(() => {
    if (isFetchingInvoiceCycle) return
    if (
      billingType === EnumTipoFacturacion.Refacturacion &&
      (reInvoiceTypeState === EnumTipoRefacturacion.AjusteProxima || !generateInvoice)
    ) {
      fetchAdjustmentConceptsData()
    }
  }, [
    isFetchingInvoiceCycle,
    billingType,
    generateInvoice,
    reInvoiceTypeState,
    fetchAdjustmentConceptsData
  ])

  const fetchSettlementInvoicesAnnouncements = useCallback(async () => {
    setIsFetchingKpis(true)
    await getSettlementsInvoicesLandingKpis(executionCycleId)
      .then(res => {
        if (res.success && res.data) {
          setLandingKpis(res.data)
          if (res.data.totalInvoicesGenerated < envConfig.invoiceCycleConfig.maxQueryItems) {
            setSettlementsIsBelowMaxQueryLimit(true) // to prevent big queries
          }
          if (res.data.totalInvoicesGenerated > 0) {
            setSettlementsIsEmpty(false)
          }
        }
      })
      .finally(() => setIsFetchingKpis(false))
  }, [executionCycleId])

  const fetchInvoiceCycleInfo = async (id: number): Promise<boolean> => {
    setIsFetchingInvoiceCycle(true)
    return await getInvoiceCycleByID(id).then(res => {
      if (res.success && res.data) {
        const invoiceCycle = res.data
        setInvoiceCycleType(invoiceCycle.invoiceCycleType)
        setSubjectType(invoiceCycle.subjectType)
        setBillingType(invoiceCycle.invoiceType)
        setGenerateInvoice(!!invoiceCycle.generateInvoice)
        setInvoiceCycleDescription(invoiceCycle.description)
        setIsFetchingInvoiceCycle(false)
        return true
      }
      setIsFetchingInvoiceCycle(false)
      return false
    })
  }

  const patchTerminateObjection = async (row: ObjectionTableModel) => {
    await terminateObjection(row.id!).then(response => {
      if (response.success) {
        info(t('settlementsInvoices:fetchResponse.terminateObjection.success'))
      } else {
        error(t('settlementsInvoices:fetchResponse.terminateObjection.error'))
      }
    })
  }

  const handleAddComment = async (annexedParamRequest: InvoiceAnnexedParamModel) => {
    await addInvoiceAnnexedParam(invoiceRow?.invoiceId!, annexedParamRequest).then(response => {
      if (response.success) {
        info(t('settlementsInvoices:fetchResponse.addInvoiceAnnexedParam.success'))
      } else {
        error(t('settlementsInvoices:fetchResponse.addInvoiceAnnexedParam.error'))
      }
    })
  }

  const handleUpdateComment = async (
    formData: any,
    annexedParamRequest: InvoiceAnnexedParamModel
  ) => {
    await updateInvoiceAnnexedParam(
      invoiceRow?.invoiceId!,
      formData.idInvoiceAnnexedParam,
      annexedParamRequest
    ).then(response => {
      if (response.success) {
        info(t('settlementsInvoices:fetchResponse.updateInvoiceAnnexedParam.success'))
      } else {
        error(t('settlementsInvoices:fetchResponse.updateInvoiceAnnexedParam.error'))
      }
    })
  }

  // Limpiar selección cuando cambia el openCard
  useEffect(() => {
    setSelectedSettlementsData([])
  }, [openCard])

  // #endregion Hooks Ends

  return (
    <Page layout={PageLayout.SINGLE_COLUMN} fullWidth={true}>
      <PageCenterColumn>
        <PageHeader
          title={
            isFinishedExecutionCyclePage
              ? t('settlementsInvoices:header.titleFinished')
              : t('settlementsInvoices:header.title')
          }
        >
          <Button
            variant="text"
            color="primary"
            onClick={() => history.push(NonAuthRoutes.invoiceCyclesNew)}
          >
            {t('settlementsInvoices:header.buttons.goBack')}
          </Button>
          {!isFinishedExecutionCyclePage && (
            <>
              <Button variant="text" color="primary" onClick={handleRegenerateExecutionCycle}>
                {t('settlementsInvoices:header.buttons.regenerate')}
              </Button>
              <Button variant="text" color="primary" onClick={openDialog}>
                {t('settlementsInvoices:header.buttons.closeExecutionCycle')}
              </Button>
            </>
          )}
        </PageHeader>
        <LandingKpiCard
          title={invoiceCycleDescription}
          leftFlex={5}
          disableLeftMargin={true}
          customDividerBreakPoint={'sm'}
          leftContent={
            <SettlementsInvoicesKpiLeftContent
              kpis={landingKpis}
              isLoading={isFetchingKpis}
              invoiceCycleType={invoiceCycleType}
              invoiceCycleDescription={invoiceCycleDescription}
              executionData={executionData}
              isFetchingExecution={isFetchingExecution}
              activeKpi={activeKpi}
              onKpiClick={handleKpiClick}
            />
          }
          rightContent={
            <SettlementsInvoicesKpiRightContent
              kpis={landingKpis}
              isLoading={isFetchingKpis}
              activeKpi={activeKpi}
              onKpiClick={handleKpiClick}
            />
          }
          footerActions={footerButtons}
        />
        <Grid container spacing={2} style={{ marginTop: '8px' }}>
          <Grid item sm={12} md={showSearchFilter ? 5 : 12}>
            <SearchFilterCard
              showCard={showSearchFilter}
              filters={searchFilters}
              cycleState={EnumEstadoCiclo.PdteValidarFacturas}
              advancedSearch={true}
              onSearch={handleSearchFilterSubmit}
              onCancel={handleCancelSearch}
            />
          </Grid>
          <Grid item sm={12} md={showSearchFilter ? 7 : 12}>
            <GlobalObjectionsCard
              title={t('settlementsInvoices:components.objectionsCard.title')}
              className={classes.card}
              raised={false}
              show={openCard === 'objections'}
              isLoading={isFetchingObjections}
              objectionsData={objections}
              // onTerminateObjection={row => handleTerminateObjection(row)}
              hasEditPermission
            />
            {(openCard === 'settlements' || openCard === 'issued' || openCard === 'search') &&
              (isAdjustmentReInvoice ? (
                <AdjustmentConceptsTableCard
                  title={t('settlementsInvoices:components.settlementsTableCard.title')}
                  show={true}
                  data={adjustmentConceptsData}
                  isLoading={isFetchingAdjustmentConcepts}
                  onViewObjectionsClick={handleViewConceptObjections}
                />
              ) : (
                <SettlementsTableCard
                  isSettlementsInvoice={cycleStatus === EnumEstadoCiclo.PdteValidarFacturas}
                  isSettlements={cycleStatus === EnumEstadoCiclo.PdteValidarListado}
                  title={t('settlementsInvoices:components.settlementsTableCard.title')}
                  show={true}
                  data={settlementsTableData}
                  isLoading={isFetchingSettlementsData}
                  invoiceCycleType={invoiceCycleType}
                  subjectType={subjectType}
                  billingType={billingType}
                  groupingType={groupingType}
                  reInvoiceType={reInvoiceTypeState}
                  generateInvoice={generateInvoice}
                  onViewObjectionsClick={handleViewInvoiceObjections}
                  onViewCommentsClick={handleViewInvoiceComments}
                  onClickManualInvoiceActionHandler={handleManualInvoiceEdit}
                  onRegenerateClick={handleRegenerate}
                  onRepaintClick={handleRepaint}
                  onRejectClick={handleReject}
                  onRevertDiscardedClick={handleRevertDiscarded}
                  canDownload
                  cycleIsFinished={isFinishedExecutionCyclePage}
                  onTerminateObjectionsClick={handleTerminateObjectionsClick}
                  customToolbarActionsConfig={
                    customSelectedRowsMode
                      ? EnumSettlementTableCustomActionConfig.CUSTOM_SELECTION
                      : undefined
                  }
                  onExitButtonClick={handleRestartModesButtonClick}
                  onSelectionChange={handleSettlementsSelectionChange}
                />
              ))}
            {openCard === 'error' && (
              <SettlementsTableCard
                isSettlementsInvoice={true}
                isErrorSettlements={true}
                title={t('settlementsInvoices:components.settlementsTableCard.titleError')}
                show={true}
                data={settlementsTableData}
                isLoading={isFetchingSettlementsData}
                invoiceCycleType={invoiceCycleType}
                subjectType={subjectType}
                billingType={billingType}
                groupingType={groupingType}
                onViewObjectionsClick={handleViewErrorSettlementObjections}
                onViewCommentsClick={handleViewInvoiceComments}
                onClickManualInvoiceActionHandler={handleManualInvoiceEdit}
                onRegenerateClick={handleRegenerate}
                onRepaintClick={handleRepaint}
                onRejectClick={handleReject}
                onRevertDiscardedClick={handleRevertDiscarded}
                onDeleteClick={handleDeleteErrorSettlement}
                canDownload
                cycleIsFinished={isFinishedExecutionCyclePage}
                onTerminateObjectionsClick={handleTerminateObjectionsClick}
                customToolbarActionsConfig={EnumSettlementTableCustomActionConfig.ERROR_SETTLEMENT}
                onExitButtonClick={handleRestartModesButtonClick}
                onSelectionChange={handleSettlementsSelectionChange}
              />
            )}
          </Grid>
        </Grid>

        <ObjectionsDialog
          open={showObjectionsDialog}
          handleClose={() => handleObjectionsDialogClose(false)}
          title={
            objectionsDialogTabs ? t('settlementsInvoices:dialogs.objections.title') : undefined
          }
          dataTableTitle={
            objectionsDialogTabs
              ? undefined
              : t('settlementsInvoices:dialogs.invoicesObjections.title')
          }
          showDescription
          taskDomain={
            isSettlementObjections ? EnumDominioTarea.Liquidacion : EnumDominioTarea.Factura
          }
          objectionsData={isLoadingObjections ? undefined : settlementInvoicesObjections}
          tableUserPermissions={getUserPermissions(Pages.InvoicesPage)}
          hasEditPermission={hasPermission(Resources.InvoicesPage, [UserPermission.EDIT])}
          allowInsert
          onRowAdd={row =>
            isSettlementObjections
              ? onSaveObjection(row, undefined, invoiceRow?.id)
              : onSaveObjection(row, invoiceRow?.invoiceId!)
          }
          onTerminateObjection={row => handleTerminateObjection(row, true)}
          canBulkTerminate
          canModifyCriticality
          onRefresh={handleRefreshObjectionsDialogInvoices}
          tabs={objectionsDialogTabs}
        />

        <TerminateObjectionsDialog
          open={showTerminateObjectionsDialog}
          handleClose={() => setShowTerminateObjectionsDialog(false)}
          selectedRows={terminateObjectionsRows}
          domain={openCard === 'error' ? EnumDominioTarea.Liquidacion : EnumDominioTarea.Factura}
          entityIdField={openCard === 'error' ? 'id' : 'invoiceId'}
          onSuccess={handleRefreshInvoicesAfterTerminate}
        />

        <AnnexedParamCustomFormDialog
          formStateConfig={commentDialogConfig}
          onClose={handleCommentDialogClose}
          title={t('settlementsInvoices:components.commentsDialog.title')}
        />

        <CloseExecutionCycleDialog
          open={isOpen}
          onClose={closeDialog}
          hasOpenSettlements={openSettlements.length > 0}
          cycleName={invoiceCycleDescription}
          executionCycleId={executionCycleId}
          invoiceCycleId={invoiceCycleId!}
          invoiceCycleType={invoiceCycleType}
          subjectType={subjectType}
          billingType={billingType}
          onCloseWithoutInvoiceGeneration={onCloseWithoutInvoices}
          onCloseWithInvoiceGeneration={onCloseWithInvoices}
        />

        <RegenerateExecutionCycleDialog
          open={regenerateDialogOpen}
          onClose={handleRegenerateDialogClose}
          initialData={regenerateInitialData}
        />

        <CustomFormDialog {...formProps} />

        <DropzoneDialog
          open={showBulkLoadDialog}
          title={t('settlementsInvoices:components.bulkLoadDialog.title')}
          onBack={() => setShowBulkLoadDialog(false)}
          onSave={handleBulkLoadSave}
          dropzoneProps={{}}
        >
          <Box className={classes.formDialogContentCustom}>
            <Form sections={sections} data={dropzoneFormData} options={dropzoneFormOptions} />
          </Box>
        </DropzoneDialog>
      </PageCenterColumn>
    </Page>
  )
}
